// Operation checklists for each PPM type, transcribed from the official
// QDF Preventive Maintenance System Check templates in public/QDFTemplates.
// Keys are the values used by ppmType in the QDFReport schema.

export const INSPECTION_STATUS_OPTIONS = ['Pass', 'Fail', 'N/A'];

export const PPM_CHECKLISTS = {
  Weekly: [
    { sr: 10, description: 'Review RA & MS before performing maintenance' },
    { sr: 20, description: 'Inspect log for errors, unusual activity' },
    { sr: 30, description: 'Verify amplifier is powered and no-fault indicators' },
    { sr: 40, description: 'Test audio output at multiple zones' },
    { sr: 50, description: 'Check AAPPRC paging control functionality' },
    { sr: 60, description: 'Confirm DVD Player and music playback is functional' },
    { sr: 70, description: 'Visual inspection of ceiling speaker output (distortion/dead units)' },
    { sr: 80, description: 'Confirm network module (1542/1544) status LED is healthy' },
    { sr: 90, description: 'Check physical condition of all cables' },
    { sr: 100, description: 'Review work order requests submitted by shop staff (if any)' },
  ],
  Monthly: [
    { sr: 10, description: 'Review the Risk Assessment (RA) and Method Statement (MS) before performing maintenance' },
    { sr: 20, description: 'Check system log: Clean, Voltage, LAN' },
    { sr: 30, description: 'Visual check for controller condition and cable connection integrity' },
    { sr: 40, description: 'Amplifier: Check power-on, fan sound, LED indicators' },
    { sr: 50, description: 'Amplifier: Measure output dB level and compare with baseline' },
    { sr: 60, description: 'Network Module (1544 / 1542): Check connectivity and status LEDs' },
    { sr: 70, description: 'Audio Converter TSX-THL: Check power, audio transmission, and heat' },
    { sr: 80, description: 'DVD Music Player: Check playback and all track functions' },
    { sr: 90, description: 'DVD Player: Clean lens and body with appropriate tools' },
    { sr: 100, description: 'Priority Paging Remote Controller (AAPPRC): Check all zones function' },
    { sr: 110, description: 'Ceiling Speakers: Check audio quality, ensure no distortion or dead speakers' },
    { sr: 120, description: 'Check network & audio cables for looseness or damage' },
    { sr: 130, description: 'Check power cables for heat/stress or discoloration' },
    { sr: 140, description: 'Confirm system is operating as expected' },
  ],
  Quarterly: [
    { sr: 10, description: 'Clean all device vents and filters (amplifiers, network modules, converters)' },
    { sr: 20, description: 'Re-seat and re-secure all audio, power, and network cables' },
    { sr: 30, description: 'Check for firmware/software updates on all AtlasIED devices' },
    { sr: 40, description: 'Test emergency override/paging functionality end-to-end' },
    { sr: 50, description: 'Test each speaker zone manually from AAPPRC and verify clear audio' },
    { sr: 60, description: 'Verify backup music source auto-failover works as intended (e.g., DVD → Network fallback)' },
    { sr: 70, description: 'Inspect all LED indicators for abnormal behavior or logs' },
    { sr: 80, description: 'Physically inspect racks and shelving for wear/tear, bent panels, or missing screws' },
    { sr: 90, description: 'Validate system time and schedule-based triggers (e.g., auto on/off events)' },
    { sr: 100, description: 'Run full-zone announcement to verify clarity and consistency across all areas' },
    { sr: 110, description: 'Confirm grounding integrity (amplifiers, rack frame, network devices)' },
    { sr: 120, description: 'Check UPS (if present) battery condition and backup run time' },
    { sr: 130, description: 'Document any anomalies, log warnings, or errors detected during the check' },
    { sr: 140, description: 'Verify rack labeling, cable tagging, and asset tags are still readable and intact' },
    { sr: 150, description: 'Check device heat levels (hand test or thermal camera if available)' },
  ],
  'Bi-Annually': [
    { sr: 10, description: 'Perform full power cycle of each device (amplifiers, converters, network modules)' },
    { sr: 20, description: 'Inspect and clean internal components (fan blades, heat sinks, filters) if accessible' },
    { sr: 30, description: 'Re-test all device input/output ports for wear, looseness, or signal drop' },
    { sr: 40, description: 'Run tone test through all zones and measure output using SPL meter (log dB range)' },
    { sr: 50, description: 'Inspect speaker grills, mounts, and ceiling tiles for structural integrity' },
    { sr: 60, description: 'Audit speaker zoning maps against current system layout (flag any undocumented changes)' },
    { sr: 70, description: 'Validate Dante or CobraNet routing paths (if applicable) and re-sync if needed' },
    { sr: 80, description: 'Test network switch or hub performance (latency, link quality)' },
    { sr: 90, description: 'Clean DVD Player lens, check disc tray motor response, test looping playback' },
    { sr: 100, description: 'Check for electrical grounding noise or hum in speakers during silent periods' },
    { sr: 110, description: 'Test paging mic sensitivity and frequency response across multiple zones' },
    { sr: 120, description: 'Confirm all user access credentials are updated and unused accounts removed (if system allows)' },
    { sr: 130, description: 'Inspect rack earthing and bonding (resistance test if tools available)' },
    { sr: 140, description: 'Confirm system logs are being regularly archived and not exceeding storage limits' },
    { sr: 150, description: 'Recalibrate or test ANS (ambient noise sensors) if available' },
  ],
  Annually: [
    { sr: 10, description: 'Perform full audit of all installed devices: amps, AAPPRCs, converters, DVD players, network units' },
    { sr: 20, description: 'Validate and document current firmware versions of all connected devices' },
    { sr: 30, description: 'Apply any critical firmware updates recommended by OEMs (with proper backup)' },
    { sr: 40, description: 'Measure and log output SPL for each zone; compare to design baseline' },
    { sr: 50, description: 'Confirm all equipment serial numbers match asset registry records' },
    { sr: 60, description: 'Inspect all speaker units (ceiling/wall-mounted) for damage, discoloration, or grill corrosion' },
    { sr: 70, description: 'Replace any visibly worn or degraded cables, connectors, or patch panels' },
    { sr: 80, description: 'Conduct full paging test across all zones — simulate emergency and normal scenarios' },
    { sr: 90, description: 'Confirm UPS runtime under load — test actual switchover scenario' },
    { sr: 100, description: 'Verify that physical and digital zone labeling is accurate and up to date' },
    { sr: 110, description: 'Perform system restart test to ensure all services auto-recover as expected' },
    { sr: 120, description: 'Audit access logs or control panel usage (if system supports this)' },
    { sr: 130, description: 'Inspect and retighten rack mounting points, grounding bars, and enclosure panels' },
    { sr: 140, description: 'Run structured cabling certification test (if available) for long runs and trunk lines' },
    { sr: 150, description: 'Archive a full-year maintenance report and attach all related work order references' },
  ],
};

export const getChecklistForPpmType = (ppmType) => PPM_CHECKLISTS[ppmType] || [];

// Build a blank inspection object for a given PPM type. Pure helper so the
// component, validation, and PDF code all start from the same shape.
export const buildEmptyInspection = (ppmType) => ({
  ppmType: ppmType || null,
  items: getChecklistForPpmType(ppmType).map((row) => ({
    sr: row.sr,
    description: row.description,
    status: '', // '' | 'Pass' | 'Fail' | 'N/A'
    remarks: 'checked',
  })),
  spareParts: [], // [{ deviceName, description, quantity, remarks }]
  comments: '',
  preparedBy: '',
  completedAt: null,
});

// Summarize results for badges / validation.
export const summarizeInspection = (inspection) => {
  const items = inspection?.items || [];
  const counts = { pass: 0, fail: 0, na: 0, pending: 0 };
  items.forEach((it) => {
    if (it.status === 'Pass') counts.pass += 1;
    else if (it.status === 'Fail') counts.fail += 1;
    else if (it.status === 'N/A') counts.na += 1;
    else counts.pending += 1;
  });
  return {
    ...counts,
    total: items.length,
    isComplete: items.length > 0 && counts.pending === 0,
    hasFailures: counts.fail > 0,
  };
};
